import { ActionType } from '../flows/actions/action'
import { StepOutput, StepOutputStatus } from './execution/step-output'

/** Where a step's record sits in a run's steps: top level, or inside loop iterations (outermost first) */
export type RunStepLocation = {
    stepName: string
    iterations: { loopName: string, index: number }[]
}

const iterationsOf = (step: StepOutput): Record<string, StepOutput>[] =>
    step.type === ActionType.LOOP_ON_ITEMS
        ? (step.output?.iterations ?? [])
        : []

/**
 * Whether `step` itself failed in a way worth reporting: the last nested
 * failure inside its own iterations (if it has any) takes priority over the
 * step's own status — a loop marked FAILED because one of its iterations
 * failed is reported as that iteration's step, not the loop.
 */
const failureCandidate = (
    stepName: string,
    step: StepOutput,
    trail: RunStepLocation['iterations'],
): RunStepLocation | null => {
    const nested = iterationsOf(step).reduce<RunStepLocation | null>(
        (last, iterationSteps, index) => findFailedStep(iterationSteps, [...trail, { loopName: stepName, index }]) ?? last,
        null,
    )
    if (nested) return nested
    return step.status === StepOutputStatus.FAILED ? { stepName, iterations: trail } : null
}

/**
 * The step that actually stopped the run: the LAST failure in execution
 * order, not the first. A step whose "continue on failure" option is set
 * stays recorded as FAILED, but the engine resets the run's verdict to
 * RUNNING and keeps going (`continueIfFailureHandler`,
 * `engine/src/lib/helper/error-handling.ts`) — so when several steps are
 * FAILED, every one but the last was tolerated; only the last is why the run
 * ended where it did. Descends into loop iterations; null when none failed.
 */
export const findFailedStep = (
    steps: Record<string, StepOutput>,
    trail: RunStepLocation['iterations'] = [],
): RunStepLocation | null => {
    let result: RunStepLocation | null = null
    for (const [stepName, step] of Object.entries(steps)) {
        const candidate = failureCandidate(stepName, step, trail)
        if (candidate) result = candidate
    }
    return result
}

/** Does `stepName` appear anywhere in `steps`, directly or nested inside a loop's body? */
const containsStepDeep = (steps: Record<string, StepOutput>, stepName: string): boolean =>
    stepName in steps ||
    Object.values(steps).some((step) => iterationsOf(step).some((it) => containsStepDeep(it, stepName)))

const iterationHasFailure = (steps: Record<string, StepOutput>, stepName: string): boolean =>
    steps[stepName]?.status === StepOutputStatus.FAILED ||
    Object.values(steps).some((step) => iterationsOf(step).some((it) => iterationHasFailure(it, stepName)))

export type LocatedStepRecord = {
    record: StepOutput
    location: RunStepLocation
    iterationCount: number | null
    /**
     * The last failing iteration of `stepName` within the loop directly containing
     * it, independent of which iteration was actually inspected; null when
     * `stepName` is not in a loop, or none of its iterations failed.
     */
    failingIteration: number | null
}

const locate = (
    steps: Record<string, StepOutput>,
    stepName: string,
    iteration: number | undefined,
    trail: RunStepLocation['iterations'],
    iterationCount: number | null,
    failingIteration: number | null,
): LocatedStepRecord | null => {
    const direct = steps[stepName]
    if (direct) {
        return { record: direct, location: { stepName, iterations: trail }, iterationCount, failingIteration }
    }
    for (const [loopName, step] of Object.entries(steps)) {
        const iterations = iterationsOf(step)
        if (iterations.length === 0 || !iterations.some((it) => containsStepDeep(it, stepName))) continue
        // `iteration` addresses the loop directly wrapping stepName (the
        // innermost one); an outer ancestor loop always picks its own
        // failing-or-last iteration to descend through
        const isDirectParent = iterations.some((it) => stepName in it)
        // The last failing iteration, as `findFailedStep` answers: earlier ones were tolerated
        const failingIndex = iterations.map((it) => iterationHasFailure(it, stepName)).lastIndexOf(true)
        const thisLoopFailingIteration = failingIndex === -1 ? null : failingIndex
        const defaultIndex = failingIndex === -1 ? iterations.length - 1 : failingIndex
        const wanted = isDirectParent ? iteration ?? defaultIndex : defaultIndex
        const boundedIndex = Math.min(Math.max(wanted, 0), iterations.length - 1)
        return locate(
            iterations[boundedIndex],
            stepName,
            isDirectParent ? undefined : iteration,
            [...trail, { loopName, index: boundedIndex }],
            isDirectParent ? iterations.length : iterationCount,
            isDirectParent ? thisLoopFailingIteration : failingIteration,
        )
    }
    return null
}

/**
 * The record of `stepName`; inside a loop, the given iteration of the
 * innermost loop, else the last failing one, else the last. Null when `stepName`
 * is not found anywhere in `steps`.
 */
export const locateStepRecord = (
    steps: Record<string, StepOutput>,
    stepName: string,
    iteration?: number,
): LocatedStepRecord | null => locate(steps, stepName, iteration, [], null, null)
